import React from "react";
import {OrgRequest, OrgRequestRestControllerApiFactory} from "arteco-api-client-ts";
import {FormContent} from "../../../../components/form/form_inputs/form_buttons";
import {FormInput} from "../../../../components/form/form_inputs/form_input";
import FormSelect from "../../../../components/form/form_inputs/form_select";
import FormAutocomplete from "../../../../components/form/form_inputs/form_autocomplete";
import FormAutocompleteMulti from "../../../../components/form/form_inputs/form_autocomplete_multi";
import {Box, Grid, Stack, Typography} from "@mui/material";
import {useForm, UseFormProps} from "../../../../components/form/form_model";
import {useApiManager} from "../../../../components/utils/api";
import {types} from "../../../../components/form/types";
import {RecordAttachments} from "../../../../components/record_attachments";
import {capSourceText, HoursCapInput, HoursMeter, IssueChips, IssueIdsInput} from "../presales/ui";
import {dateToString} from "../../../../components/utils/dates";

export const OrgRequestEditPage = () => {
    const apiManager = useApiManager();
    const ctrl = apiManager.factory(OrgRequestRestControllerApiFactory);

    const form = useForm({
        fetcher: (id) => ctrl.getOrgRequest(id),
        updater: (bean) => ctrl.saveOrgRequest(bean),
        remover: (id) => ctrl.deleteOrDismissOrgRequest(id),
        factory: () => ({} as OrgRequest),
    } as UseFormProps<OrgRequest>);

    return (
        <>
        <FormContent form={form} title={"Formulario de oportunidad"}>
            <FormInput field={"title"} label={"Título"}/>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <FormAutocomplete field={"organization"} label={"Organización"} type={types.organizations}/>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <FormAutocomplete field={"contact"} label={"Contacto"} type={types.contacts}/>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <FormInput field={"requested"} label={"Fecha"} type="date"/>
                </Grid>
            </Grid>

            {/* Pipeline comercial (#11265). Los cuatro campos son opcionales: una necesidad
                recién anotada no tiene importe ni fecha de cierre, y obligar a inventárselos
                daría un forecast peor que no tener el dato. Si el estado se deja vacío, el
                backend lo guarda como DETECTED. */}
            <Grid container spacing={3}>
                <Grid item xs={12} sm={3}>
                    <FormSelect field={"status"} label={"Estado"} type={types.orgRequestStatuses}/>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <FormInput field={"amount"} label={"Importe (sin IVA)"} type="number"/>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <FormInput field={"closeDate"} label={"Cierre (previsto o real)"} type="date"/>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <FormInput field={"probability"} label={"Probabilidad %"} type="number"/>
                </Grid>
            </Grid>

            {/* La fecha que hace que alguien vuelva a mirar esta oportunidad (#11593). No es el
                cierre: el cierre dice cuándo se espera cobrar y esta, cuándo hay que volver a
                llamar. Vacía significa que nadie la vigila, y el pase diario lo canta. */}
            <Grid container spacing={3}>
                <Grid item xs={12} sm={3}>
                    <FormInput field={"nextActionDate"} label={"Próxima acción"} type="date"/>
                </Grid>
            </Grid>

            {/* Preventa (#14761, wf-preventa.md): el trabajo antes de la venta cuelga de la
                oportunidad. Sus issues de Redmine, la carpeta de la propuesta (enlace o id de
                Drive, nunca la ruta) y el tope de horas: vacío usa el de Parámetros (presales.hours-cap,
                #14767) y un valor es el tope propio de esta oportunidad. Las horas
                consumidas las lee la Extranet de Redmine; aquí solo se enseñan. */}
            <Typography variant="subtitle1" component="h2" fontWeight={700} sx={{mt: 1, mb: 1.5}}>Preventa</Typography>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    <IssueIdsInput field={"redmineIssueIds"} label={"Issues de Redmine"}/>
                </Grid>
                <Grid item xs={12} sm={5}>
                    <FormInput field={"driveFolderUrl"} label={"Carpeta de Drive (enlace o id)"}/>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <HoursCapInput field={"presalesHoursCap"} label={"Tope de horas"} effective={form.value("presalesHoursCapEffective")}/>
                </Grid>
            </Grid>
            {form.getId() && (
                <Stack direction={{xs: "column", sm: "row"}} spacing={{xs: 1.5, sm: 4}} alignItems={{sm: "center"}} sx={{mb: 3}}>
                    <Box>
                        <HoursMeter spent={form.value("presalesHoursSpent")} cap={form.value("presalesHoursCapEffective")} width={180}/>
                        <Typography variant="caption" color="text.secondary">
                            Tope: {capSourceText(form.value("presalesHoursCapEffective"), form.value("presalesHoursCap"))}
                        </Typography>
                    </Box>
                    <IssueChips ids={form.value("redmineIssueIds")} issues={form.value("presalesIssues")}/>
                    <Typography variant="caption" color="text.secondary">
                        {form.value("presalesReadAt")
                            ? "Leído de Redmine el " + dateToString(form.value("presalesReadAt")) + " a las " + String(form.value("presalesReadAt")).slice(11, 16)
                            : "Sin leer de Redmine todavía"}
                    </Typography>
                </Stack>
            )}

            <FormAutocompleteMulti field={"requestedSkills"} label={"Skills solicitadas"} type={types.skills}/>

            <FormInput field={"observation"} label={"Observaciones"} rows={4}/>
        </FormContent>
        <Box sx={{mt: 3}}>
            <RecordAttachments recordType="ORG_REQUEST" recordId={form.getId()}/>
        </Box>
        </>
    );
};
